import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lightbulb } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  useCreateManagementReview,
  useAgendaSuggestions,
} from '@/hooks/use-management-reviews';

export function ReviewsNewPage() {
  const navigate = useNavigate();
  const createMutation = useCreateManagementReview();
  const { data: suggestions } = useAgendaSuggestions();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [agenda, setAgenda] = useState('');
  const [intervalDays, setIntervalDays] = useState('90');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !date || !agenda) return;

    createMutation.mutate(
      {
        title,
        date,
        agenda,
        review_interval_days: Number(intervalDays) || 90,
      },
      {
        onSuccess: (data) => {
          navigate(`/compliance/reviews/${data.id}`);
        },
      }
    );
  };

  const applySuggestions = () => {
    if (!suggestions || suggestions.length === 0) return;
    const agendaText = suggestions
      .map((s) => `[${s.category}] ${s.item}`)
      .join('\n');
    setAgenda((prev) => (prev ? prev + '\n\n' + agendaText : agendaText));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule Management Review</h1>
          <p className="text-gray-600 mt-1">
            QP-300 Clause 8.2 — Plan and schedule a management review meeting
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Review Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Q2 2026 Safety Management Review"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Review Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interval">Review Interval (days)</Label>
                <Input
                  id="interval"
                  type="number"
                  min="1"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(e.target.value)}
                />
                <p className="text-xs text-gray-500">Default: 90 days (quarterly)</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="agenda">Agenda *</Label>
                {suggestions && suggestions.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={applySuggestions}
                  >
                    <Lightbulb className="h-3 w-3 mr-1" />
                    Auto-suggest
                  </Button>
                )}
              </div>
              <Textarea
                id="agenda"
                value={agenda}
                onChange={(e) => setAgenda(e.target.value)}
                placeholder="Meeting agenda items — one per line"
                rows={8}
              />
              {suggestions && suggestions.length > 0 && (
                <p className="text-xs text-gray-500">
                  {suggestions.length} suggestion(s) available based on current compliance data
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/compliance/reviews')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Scheduling...' : 'Schedule Review'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
